import { itemChannelScope, modelFamilyFromSubject, explicitlyClassified } from './janetSourceScope.js'
// src/lib/janetOffers.js — Janet's "things I can take off your plate".
//
// Pure. The worker's offers pass (api/_lib/janet-offers.js), the evening and
// morning emails, the morning text, the iMessage reply intercept in janet-ask
// and Home's offers block all read these rules, so an offer means the same
// thing on every surface.
//
// An offer is a proposal, never an act (CLAUDE.md: Janet suggests, a human
// confirms). It names the people, the specific ask and deadline, and links the
// record it came from. Accepting it hands Janet `prompt` as if the principal
// had typed it. Draft/reply offers are owned by the resumable worker; other
// offer types continue through her ordinary chat tools.
//
// The model only ever sees short source ids (e1, t3, v2…). Every link on an
// offer is rebuilt here from the evidence the pass actually gathered, so an
// offer can never carry a link to a thread Janet did not read.

export const OFFER_ACTIONS = ['prep_brief', 'call_brief', 'draft_reply', 'checklist', 'send_promised', 'follow_up', 'other']
export const MAX_OFFERS = 5
export const OFFER_STATUSES = ['open', 'accepted', 'dismissed', 'superseded']

const ACTION_VERB = {
  prep_brief: 'Build the brief',
  call_brief: 'Build the call brief',
  draft_reply: 'Draft the reply',
  checklist: 'Make the checklist',
  send_promised: 'Get it sent',
  follow_up: 'Draft the follow-up',
  other: 'Do it',
}

export function defaultActionLabel(actionType) {
  return ACTION_VERB[actionType] || ACTION_VERB.other
}

/** Gmail's own address for a thread, in the mailbox it was read from. */
export function gmailThreadUrl(threadId, mailbox) {
  if (!threadId) return null
  const auth = mailbox ? `?authuser=${encodeURIComponent(mailbox)}` : ''
  return `https://mail.google.com/mail/${auth}#all/${threadId}`
}

/** Where "Do it" goes in the app: Janet's chat, which runs the offer. */
export function offerDoItPath(offerId) {
  return `/janet/chat?offer=${encodeURIComponent(offerId)}`
}

/**
 * The single thread an offer's sources resolve to, when there is exactly
 * one — null when there are zero or more than one distinct thread. Used to
 * stamp `janet_offers.origin_thread_id`, the atomic one-active-offer-per-
 * thread backstop (supabase/migrations/20261018_janet_reply_alerts.sql,
 * issue #251 review round 3, finding #1): a reply alert always resolves to
 * exactly one (api/_lib/janet-reply-alerts.js is single-thread by design);
 * a twice-daily offer (api/_lib/janet-offers.js) only when its gathered
 * evidence happened to produce a single thread source.
 */
export function singleThreadId(sources) {
  const ids = new Set((sources || []).map(s => s?.thread_id).filter(Boolean))
  return ids.size === 1 ? [...ids][0] : null
}

function clean(s, max) {
  const t = String(s ?? '').replace(/\s+/g, ' ').trim()
  return max && t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t
}

function validDate(v) {
  if (!v) return null
  const d = new Date(v)
  return Number.isFinite(d.getTime()) ? d.toISOString() : null
}

/**
 * Shape-check the model's offers against the evidence it was given.
 *
 * - Source ids the pass never gathered are dropped; an offer left with no real
 *   source is dropped (an offer is grounded in the record or it is nothing).
 * - Unknown action types become 'other'; title and prompt are required.
 * - Ranked in the model's order, capped at MAX_OFFERS, duplicates of the same
 *   source set collapsed.
 *
 * @param {Array} raw            offers as the tool returned them
 * @param {Map<string,object>} sources  evidence id → { kind, url, label, threadId, emailId, eventId }
 */
export function normalizeOffers(raw, sources, { max = MAX_OFFERS, familyEnabled = true, principalId, onScopeUnavailable = () => {} } = {}) {
  const out = []
  const seen = new Set()
  for (const o of Array.isArray(raw) ? raw : []) {
    if (!o || typeof o !== 'object') continue
    const title = clean(o.title, 140)
    const prompt = clean(o.instruction ?? o.prompt, 1500)
    if (!title || !prompt) continue
    const ids = [...new Set((Array.isArray(o.source_ids) ? o.source_ids : []).map(String))]
    const refs = ids.map(id => sources.get(id)).filter(Boolean)
    if (!refs.length || refs.length !== ids.length) { onScopeUnavailable(); continue }
    const key = refs.map(r => r.key || r.label).sort().join('|')
    if (seen.has(key)) continue
    seen.add(key)
    const scope = itemChannelScope(refs, principalId, modelFamilyFromSubject(o))
    if (!scope.accessAllowed) { onScopeUnavailable(); continue }
    // An explicit "mixed" or "uncertain" is a real answer, excluded while family
    // is off; only a missing classification means the scope was unavailable.
    if (!familyEnabled && scope.family !== false) { if (scope.family === null && !explicitlyClassified(o)) onScopeUnavailable(); continue }
    const actionType = OFFER_ACTIONS.includes(o.action_type) ? o.action_type : 'other'
    out.push({
      rank: out.length + 1,
      title,
      why: clean(o.why, 400) || null,
      people: (Array.isArray(o.people) ? o.people : []).map(p => clean(typeof p === 'string' ? p : p?.name, 80)).filter(Boolean).slice(0, 6),
      deadline_at: validDate(o.deadline_at),
      deadline_label: clean(o.deadline_label, 80) || null,
      action_type: actionType,
      action_label: clean(o.action_label, 40) || defaultActionLabel(actionType),
      prompt,
      sources: refs.map(r => ({ family_only: scope.externalFamilyOnly, internal_scope: scope.internal, kind: r.kind, label: r.label, url: r.url || null, thread_id: r.threadId || null, ...(r.missedAsk ? { missed_ask: true } : {}), email_id: r.emailId || null, event_id: r.eventId || null })),
    })
    if (out.length >= max) break
  }
  return out
}

/**
 * What Janet is handed when an offer is accepted: the instruction plus the
 * records it rests on, by the ids her tools read (Gmail thread ids, event
 * titles). Written in the principal's voice, the way he would ask.
 */
export function acceptedOfferQuestion(offer) {
  const lines = [offer.prompt]
  const refs = (offer.sources || []).map(s => {
    if (s.kind === 'email' || s.kind === 'thread') return `- Email thread "${s.label}"${s.thread_id ? ` (Gmail thread ${s.thread_id})` : ''}`
    if (s.kind === 'event') return `- Calendar: ${s.label}`
    if (s.kind === 'task') return `- Task: ${s.label}`
    if (s.kind === 'voicemail') return `- Voicemail: ${s.label}`
    return `- ${s.label}`
  })
  if (refs.length) lines.push('', 'Work from these:', ...refs)
  return lines.join('\n')
}

/**
 * A text reply that picks an offer: "1", "#2", "do 3", "3 please", "yes 1".
 * Returns the 1-based number or null. Anything longer than a short pick is a
 * real question and falls through to Janet.
 */
export function parseOfferPick(text) {
  const t = String(text || '').trim().toLowerCase()
  if (!t || t.length > 24) return null
  const m = t.match(/^(?:(?:yes|ok|okay|sure|do|go|run|take)\s*)?(?:#|no\.?\s*|number\s*)?([1-9])(?:\s*(?:please|pls|thanks|thank you|go|now))?[\s!.]*$/)
  return m ? Number(m[1]) : null
}

/** External delivery never inherits the new family scope or unknown scope. */
export function externalProfessionalItems(items) {
  return (items || []).filter(item => item && item.topic !== 'family' && item.family_only !== true &&
    Array.isArray(item.sources) && item.sources.length > 0 && item.sources.every(source => source?.family_only === false))
}

/** The morning text: the day's offers as numbered lines, reply with a number. */
export function offersText(offers, { firstName } = {}) {
  const open = externalProfessionalItems(offers).filter(o => o.status === 'open' || !o.status).slice(0, MAX_OFFERS)
  if (!open.length) return null
  const hi = firstName ? `Morning ${firstName}.` : 'Good morning.'
  // Numbered by the offer's own rank, never its position in this list: a later
  // "2" must still mean the offer that was #2 after #1 is taken elsewhere.
  const lines = open.map((o, i) => `${o.rank || i + 1}. ${o.title}${o.deadline_label ? ` (${o.deadline_label})` : ''}`)
  return `${hi} I can take these off your plate:\n${lines.join('\n')}\nReply with a number and I'll start.`
}

/** The open offer a text pick names, by rank within the newest run. */
export function offerForPick(offers, pick) {
  if (!pick) return null
  const open = (offers || []).filter(o => o.status === 'open' || !o.status)
  if (!open.length) return null
  const latest = open.reduce((a, o) => (String(o.run_at || '') > String(a) ? o.run_at : a), '')
  return externalProfessionalItems(open).find(o => (o.run_at || '') === latest && Number(o.rank) === pick) || null
}

/** Offers accepted or dismissed recently, as the next pass should read them (JUDGMENT-LEDGER §5). */
export function priorOffersDigest(rows) {
  const ruled = (rows || []).filter(r => r.status === 'accepted' || r.status === 'dismissed')
  if (!ruled.length) return ''
  return ruled.slice(0, 20).map(r => `- ${r.status === 'accepted' ? 'TAKEN' : 'DISMISSED'}: ${r.title}`).join('\n')
}

// This bounded executor only produces reviewable drafts; it never sends.
export const isDurableDraftOffer = (offer) => ['draft_reply', 'follow_up'].includes(offer?.action_type)

/**
 * The decision src/hooks/useJanetOffers.js's ruleOnOffer makes before it
 * writes: which status patch, and whether an acceptance counts as a
 * durable-draft acceptance (the `draft_` prefix on acted_via, read by
 * worker/src/jobs/janet-offer-drafts.js and rendered by
 * JanetOfferDraftStatus.jsx). Pure — no Supabase import — so this is the ONE
 * place the decision is made: the real hook calls it, and anything that
 * needs to exercise the real acceptance contract without importing a module
 * that constructs the live Supabase client at load time
 * (src/lib/supabase.js — CLAUDE.md's standing vitest/Node-script trap) calls
 * the SAME function rather than re-deriving the draft_ prefix rule itself
 * (review round 3, finding #5: "avoid duplicating logic").
 */
export function buildOfferRulingPatch(offer, status, via = 'home', { now = new Date() } = {}) {
  const actualVia = status === 'accepted' && via !== 'draft_retry' && isDurableDraftOffer(offer) ? `draft_${via}` : via
  const at = now.toISOString()
  return { status, acted_at: at, acted_via: actualVia, updated_at: at }
}
